/**
 * Capability registry (PERM-01) — the single growing list of everything a user
 * can be allowed to do in Pegasus.
 *
 * THE ONE RULE: when you build a new tab / feature / action, APPEND its
 * capability key(s) here. That's it. Because the Admin role is a wildcard
 * (`is_admin`), the owner automatically gets every new capability the moment you
 * add it — no re-granting. And every key here is automatically grantable to (or
 * revocable from) any other role/user, so every new thing is, by default, an
 * "exception" the admin can hand out or hold back. (Seth's requirement.)
 *
 * Convention: keys are `module.action`. Levels map to ESC's Screens/Reports/
 * Utilities as a ladder — `view` (see), `edit` (create/change), `manage` (admin
 * of that area). Modules mirror ESC's permission tree (Customer Information,
 * Dispatch, Service Agreements, Employees, Dashboard, Company/Utilities, Mobile).
 *
 * Roles + per-user overrides reference these keys; the DB stores the grants
 * (app_role.capabilities / app_user.grant_extra+deny). Keep this list and the
 * seeded role rows (migration perm_01_rbac_framework) in sync.
 */
export interface Capability {
  key: string;
  module: string;
  label: string;
}

export const CAPABILITIES: Capability[] = [
  // Customers (ESC: Customer Information)
  { key: "customers.view", module: "Customers", label: "View customers" },
  { key: "customers.edit", module: "Customers", label: "Add / edit customers" },
  { key: "contacts.edit", module: "Customers", label: "Add / edit customer contacts" },
  { key: "customers.mark_inactive", module: "Customers", label: "Mark accounts inactive / reactivate" },
  { key: "customers.merge", module: "Customers", label: "Merge duplicate accounts (reversible, sensitive)" },
  // 360-AUDIT wave 2 (C): the sensitive 360 writes get their own keys so the
  // FAIL_OPEN flip at rollout locks them down per-role, not all-or-nothing.
  { key: "customers.transfer_owner", module: "Customers", label: "Record a home sale (transfer the account to a new owner)" },
  { key: "portal.manage", module: "Customers", label: "Customer portal: send, reset + toggle links" },
  { key: "equipment.edit", module: "Customers", label: "Edit equipment records (mark removed / replaced)" },

  // Board / Dispatch (ESC: Dispatch)
  { key: "board.view", module: "Board", label: "View the board" },
  { key: "board.view_self", module: "Board", label: "View own schedule only" },
  { key: "board.manage", module: "Board", label: "Plan + optimize routes" },
  { key: "dispatch.reassign", module: "Board", label: "Reassign calls between techs" },
  { key: "dispatch.delete", module: "Board", label: "Delete a dispatch (recoverable)" },

  // Agreements (ESC: Service Agreements)
  { key: "agreements.view", module: "Agreements", label: "View agreements" },
  { key: "agreements.edit", module: "Agreements", label: "Create / cancel / transfer agreements" },

  // Team / Techs (ESC: Employees) — incl. availability (TECH-AVAIL-01)
  { key: "team.view", module: "Team", label: "View the tech roster" },
  { key: "team.view_self", module: "Team", label: "View own record only" },
  { key: "team.edit", module: "Team", label: "Edit tech records" },
  { key: "team.mark_sick", module: "Team", label: "Mark a tech out sick (same-day)" },
  { key: "team.add_timeoff", module: "Team", label: "Add time off / meetings / errands" },
  { key: "team.request_timeoff", module: "Team", label: "Request time off (own)" },
  { key: "team.approve_timeoff", module: "Team", label: "Approve / deny vacation requests" },
  { key: "team.edit_skills", module: "Team", label: "Edit skills + certifications" },
  { key: "team.pay", module: "Team", label: "View + edit pay + commission (sensitive)" },
  { key: "team.install_week", module: "Team", label: "Install Week: adjust openings + mark days full (coordinator)" },
  // BULLETIN-01 (Seth 2026-09-01): the day's message — posted from the office
  // Today tab, shown on every tech's phone. Seeded to dispatcher + office.
  { key: "team.bulletin", module: "Team", label: "Post the day's bulletin (Today tab + field app)" },

  // Schedule queue / pending tickets (TICKET-01 — replaces ESC's holding buckets)
  { key: "queue.view", module: "Board", label: "See the pending-ticket queue" },
  { key: "queue.add_ticket", module: "Board", label: "Add pending tickets" },
  { key: "queue.resolve", module: "Board", label: "Schedule / cancel pending tickets" },

  // Comms (COMMS-02 — the message thread; COMMS-04 — sending)
  { key: "comms.log", module: "Customers", label: "Log calls / messages on the thread" },
  { key: "comms.send", module: "Customers", label: "Send email / messages to customers" },

  // Calls (CALLS-00 — the live call dock; locked: everyone by default,
  // revocable per user; DISPATCHERS KEEP THIS ON)
  { key: "calls.dock", module: "Calls", label: "See the live incoming-call dock" },
  // PHONE-SOFT-01: the browser softphone. NOT seeded to any role — the admin
  // wildcard has it (Seth pilots it), everyone else gets it per-user via
  // grant_extra once the test number has proven quiet. Having the capability
  // still isn't a ringing phone: each user turns theirs on in the dock, which
  // mints their Telnyx credential.
  { key: "calls.softphone", module: "Calls", label: "Browser softphone — answer Sedgwick calls in Pegasus" },

  // Team chat (CHAT-01 desktop dock + M1a phone tab). Seth 2026-08-28: "keep it
  // behind permissions… but please approve it for all techs for now" — seeded to
  // EVERY role (migration chat_perm_01), so turning it off is a per-user "Never"
  // in Settings → Users & Roles.
  { key: "chat.use", module: "Chat", label: "Team chat (desktop bubble + phone Chat tab)" },

  // Reports (ESC: Dashboard). reports.view gates the SCREEN; each report is
  // its own grantable capability (Seth 2026-06-12: "set which users get
  // access to what reports"). RULE: a new report in ReportsView registers a
  // `reports.<key>` capability here AND gets seeded to the roles that should
  // see it (see migration perm_05_per_report_caps for the pattern).
  { key: "reports.view", module: "Reports", label: "Open the Reports screen" },
  { key: "reports.dispatch-list", module: "Reports", label: "Report: Dispatch List" },
  { key: "reports.invoice-list", module: "Reports", label: "Report: Invoice List" },
  { key: "reports.po-list", module: "Reports", label: "Report: Purchase Order List" },
  { key: "reports.vendor-list", module: "Reports", label: "Report: Vendors" },
  { key: "reports.ticket-list", module: "Reports", label: "Report: Ticket History" },
  { key: "reports.revenue", module: "Reports", label: "Report: Revenue & Sales" },
  { key: "reports.margin", module: "Reports", label: "Report: Margin & Profitability" },
  { key: "reports.dispatch", module: "Reports", label: "Report: Dispatch Volume" },
  { key: "reports.equipment-installs", module: "Reports", label: "Report: Equipment Installs" },
  { key: "reports.seasonal-demand", module: "Reports", label: "Report: Seasonal Demand" },
  { key: "reports.agreements", module: "Reports", label: "Report: Agreements" },
  { key: "reports.tech-payroll", module: "Reports", label: "Report: Tech Hours & Tickets (payroll)" },
  { key: "reports.collections", module: "Reports", label: "Report: Collection Manager" },
  { key: "reports.membership-renewals", module: "Reports", label: "Report: Plan Renewals (expiring + lapsed)" },
  { key: "reports.tuneups-due", module: "Reports", label: "Report: Tune-ups Due (membership auto-scheduled)" },
  { key: "reports.parts-day", module: "Reports", label: "Report: Parts for the Day (per-tech truck load)" },
  { key: "reports.customers", module: "Reports", label: "Report: All Customers (audience builder)" },
  { key: "reports.serial-lookup", module: "Reports", label: "Report: Serial Lookup" },
  { key: "reports.warranty-expiring", module: "Reports", label: "Report: Warranty Expiring" },
  { key: "reports.refrigerant", module: "Reports", label: "Report: Refrigerant Log (stock, tanks, EPA recovery record)" },
  { key: "reports.stock-counts", module: "Reports", label: "Report: Stock Counts (cycle-count history + drift)" },
  { key: "reports.ask", module: "Reports", label: "Report: Ask Pegasus (natural-language)" },
  { key: "reports.insights", module: "Reports", label: "Report: AI Insights" },
  { key: "reports.classification-review", module: "Reports", label: "Report: Model Classifications" },

  // Marketing (MKT-REDESIGN-01 decision "Who can send", 2026-09-03): anyone
  // who can open Marketing may build audiences and preview; SCHEDULING,
  // releasing or cancelling a campaign needs this — enforced by the campaign
  // RPCs + pegasus_campaign policies too (pegasus_require_cap), not just the UI.
  { key: "marketing.send", module: "Marketing", label: "Send campaigns (schedule, release, cancel)" },

  // Accounting (ESC: Sales / Receivables / Banking) — ESC-owned for now (DEC-22)
  { key: "accounting.view", module: "Accounting", label: "View AR / invoices (read-only)" },
  { key: "collections.edit", module: "Accounting", label: "Add collection notes & promises" },
  { key: "invoices.delete", module: "Accounting", label: "Delete Pegasus invoices" },
  { key: "payments.collect", module: "Accounting", label: "Collect payments (card / cash / check)" },
  // FIELD-PAY-01 (Seth 2026-09-24): installers collect the balance on THEIR job —
  // never more than what's due, no refunds, no invoice collections. Techs have it.
  { key: "payments.collect_field", module: "Accounting", label: "Collect a job's balance in the field (installers)" },
  { key: "payments.refund", module: "Accounting", label: "Issue refunds (sensitive)" },

  // Settings / Admin (ESC: Company / Utilities)
  { key: "settings.manage", module: "Settings", label: "Manage app settings" },
  { key: "users.manage", module: "Settings", label: "Manage users, roles + permissions" },
  { key: "audit.view", module: "Settings", label: "View the audit log + undo changes (admin)" },
  { key: "automations.manage", module: "Settings", label: "Edit comms automations (toggles + templates)" },

  // Mobile (ESC: Login as mobile user / Mobile permissions) — a capability,
  // NOT a paid license (deliberate departure from ESC).
  { key: "mobile.access", module: "Mobile", label: "Use the Pegasus mobile app" },
  // MGR-01 (Seth 2026-08-20/31): weekend/after-hours managers see the WHOLE
  // operation from the phone — full board for any date, every tech's day,
  // completed history. Grant per-manager; never techs; Admin is the wildcard.
  { key: "mobile.manager", module: "Mobile", label: "Manager view — whole board + history on the phone" },
  { key: "mobile.sell", module: "Mobile", label: "Selling tech: install week + price book in the field app" },
];

/** Fast lookup + a guard against typo'd keys in code. */
export const CAPABILITY_KEYS: ReadonlySet<string> = new Set(
  CAPABILITIES.map((c) => c.key),
);

/** Capabilities grouped by module — for the future admin Users/Roles screen. */
export function capabilitiesByModule(): Record<string, Capability[]> {
  const out: Record<string, Capability[]> = {};
  for (const c of CAPABILITIES) (out[c.module] ??= []).push(c);
  return out;
}
